import type { SQLiteDatabase } from 'expo-sqlite';

import { parseRegionHazards } from '@/data/region-hazards';
import { SYSTEM_CATALOG } from '@/data/systems';
import { newId, now } from '@/db/ids';
import { bumpDataRevision } from '@/lib/data-revision';
import {
  cancelAllDigests,
  cancelAllSystemReminders,
  cancelAllTaskReminders,
  cancelTaskReminder,
} from '@/lib/notifications';
import {
  getExteriorMaterials,
  getFoundations,
  getRoofMaterials,
} from '@/lib/property-attrs';
import type {
  Difficulty,
  PropertyRow,
  SubscriptionTier,
  SystemLocation,
  SystemRow,
  TaskRow,
  Tier,
  UserSettingsRow,
} from '@/db/types';
import {
  generatePlanFromSystems,
  projectAnnualPlan,
  type PlannedTask,
} from '@/lib/plan';

export interface ActiveTask extends TaskRow {
  system_kind: string | null;
  system_subtype: string | null;
  system_nickname: string | null;
  /**
   * For tasks pulled by `getCompletedTasks`, the kind of the most recent
   * task_completion row ('done' | 'skip'). Undefined when the task is
   * still active (no completion rows yet) or when the query that loaded
   * it didn't include the completion-kind subquery.
   */
  completion_kind?: 'done' | 'skip' | null;
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const MONTH_MS = 30 * 24 * 60 * 60 * 1000;

const ACTIVE_TASK_SELECT = `
  SELECT t.*, s.kind AS system_kind, s.subtype AS system_subtype, s.nickname AS system_nickname
  FROM task t
  LEFT JOIN system s ON s.id = t.system_id
`;

/**
 * Resolve the property the UI should treat as "current."
 *
 * v38 semantics: prefer the property whose id matches
 * `user_settings.active_property_id`. If that's NULL (fresh installs
 * or upgrades from v37 that haven't picked yet) OR if the id no longer
 * points at a live property (the user deleted their active home),
 * fall back to the oldest setup-complete property — same as the v37
 * single-home heuristic.
 *
 * Note: we deliberately use `created_at ASC` for the fallback (not
 * `updated_at DESC` as v37 did). With multi-home, an edit on a
 * secondary home shouldn't suddenly become the "active" one — the
 * primary / first-onboarded home is the safer default until the user
 * explicitly switches.
 */
export async function getActiveProperty(db: SQLiteDatabase): Promise<PropertyRow | null> {
  const settings = await db.getFirstAsync<UserSettingsRow>(
    `SELECT * FROM user_settings WHERE id = 'singleton'`
  );
  if (settings?.active_property_id) {
    const active = await db.getFirstAsync<PropertyRow>(
      `SELECT * FROM property
       WHERE id = ? AND setup_complete = 1 AND deleted_at IS NULL`,
      settings.active_property_id
    );
    if (active) return active;
    // Stale pointer (active home was deleted) — fall through to default.
  }
  return await db.getFirstAsync<PropertyRow>(
    `SELECT * FROM property
     WHERE setup_complete = 1 AND deleted_at IS NULL
     ORDER BY created_at ASC LIMIT 1`
  );
}

/**
 * List every setup-complete home the user owns, in creation order.
 * Drives the HomeSwitcher list + the 5-home cap check.
 */
export async function listProperties(db: SQLiteDatabase): Promise<PropertyRow[]> {
  return await db.getAllAsync<PropertyRow>(
    `SELECT * FROM property
     WHERE setup_complete = 1 AND deleted_at IS NULL
     ORDER BY created_at ASC`
  );
}

/**
 * Switch the active home. Writes the pointer + bumps the data revision
 * so every property-scoped screen (Home ring, Schedule, My Home, sidebar,
 * Emergency Card, etc.) refetches against the new id without a restart.
 *
 * The id is validated against a real, non-deleted property — passing a
 * stale or unknown id is a no-op (so the worst that happens on a
 * malformed deep-link is the active home doesn't change).
 */
/**
 * Counts of user-visible items tied to a property — surfaces in the
 * delete-home confirm dialog so the user sees exactly how much data
 * they're about to lose ("12 systems, 84 tasks, 7 documents...").
 * All counts are over the active (non-soft-deleted) rows; the dialog
 * is the user's only chance to back out, so under-counting would
 * understate the cost.
 */
export interface PropertyContentSummary {
  systems: number;
  tasks: number;
  documents: number;
  expenses: number;
  inventory: number;
  contacts: number;
  goals: number;
}

export async function getPropertyContentSummary(
  db: SQLiteDatabase,
  propertyId: string
): Promise<PropertyContentSummary> {
  const one = async (sql: string): Promise<number> => {
    const row = await db.getFirstAsync<{ n: number }>(sql, propertyId);
    return row?.n ?? 0;
  };
  const [systems, tasks, documents, expenses, inventory, contacts, goals] =
    await Promise.all([
      one(
        `SELECT COUNT(*) AS n FROM system WHERE property_id = ? AND deleted_at IS NULL`
      ),
      one(
        `SELECT COUNT(*) AS n FROM task WHERE property_id = ? AND deleted_at IS NULL`
      ),
      one(
        `SELECT COUNT(*) AS n FROM document WHERE property_id = ? AND deleted_at IS NULL`
      ),
      one(
        `SELECT COUNT(*) AS n FROM expense WHERE property_id = ? AND deleted_at IS NULL`
      ),
      one(
        `SELECT COUNT(*) AS n FROM inventory_item WHERE property_id = ? AND deleted_at IS NULL`
      ),
      one(
        `SELECT COUNT(*) AS n FROM contact WHERE property_id = ? AND deleted_at IS NULL`
      ),
      one(
        `SELECT COUNT(*) AS n FROM goal WHERE property_id = ? AND deleted_at IS NULL`
      ),
    ]);
  return { systems, tasks, documents, expenses, inventory, contacts, goals };
}

/** Format the summary into a single human-readable line for the delete
 *  dialog. Lists only non-zero categories so empty homes get a clean
 *  short message instead of "0 systems, 0 tasks, 0 ..." padding. */
export function formatContentSummary(s: PropertyContentSummary): string {
  const parts: string[] = [];
  const push = (n: number, singular: string, plural: string) => {
    if (n > 0) parts.push(`${n} ${n === 1 ? singular : plural}`);
  };
  push(s.systems, 'system', 'systems');
  push(s.tasks, 'task', 'tasks');
  push(s.documents, 'document', 'documents');
  push(s.expenses, 'expense', 'expenses');
  push(s.inventory, 'inventory item', 'inventory items');
  push(s.contacts, 'contact', 'contacts');
  push(s.goals, 'goal / project', 'goals / projects');
  return parts.length === 0 ? 'No data yet' : parts.join(' · ');
}

export async function setActiveProperty(
  db: SQLiteDatabase,
  propertyId: string
): Promise<void> {
  const exists = await db.getFirstAsync<{ id: string }>(
    `SELECT id FROM property WHERE id = ? AND deleted_at IS NULL`,
    propertyId
  );
  if (!exists) return;
  await db.runAsync(
    `UPDATE user_settings
     SET active_property_id = ?, updated_at = ?
     WHERE id = 'singleton'`,
    propertyId,
    Date.now()
  );
  bumpDataRevision();
}

/** Read the current subscription tier. Free = 1 home cap; paid = 5. */
export async function getSubscriptionTier(
  db: SQLiteDatabase
): Promise<SubscriptionTier> {
  const settings = await db.getFirstAsync<UserSettingsRow>(
    `SELECT * FROM user_settings WHERE id = 'singleton'`
  );
  return settings?.tier ?? 'paid';
}

/** Maximum number of homes by tier. Single source of truth for the cap. */
export const HOME_CAP: Record<SubscriptionTier, number> = {
  free: 1,
  paid: 5,
};

/**
 * Every task (completed + active, undeleted) whose due_at falls in the
 * given calendar year. Used by the Annual Home Tasks PDF so the
 * printable record shows the full year end-to-end — past completions
 * + upcoming work — instead of just the next ~200 active tasks. Cap
 * at 2000 to keep the PDF render bounded; a single home generating
 * more than that is a bug elsewhere.
 */
export async function getTasksForYear(
  db: SQLiteDatabase,
  propertyId: string,
  year: number
): Promise<ActiveTask[]> {
  const from = new Date(year, 0, 1).getTime();
  const to = new Date(year + 1, 0, 1).getTime();
  return await db.getAllAsync<ActiveTask>(
    `${ACTIVE_TASK_SELECT}
     WHERE t.property_id = ?
       AND t.deleted_at IS NULL
       AND t.due_at >= ?
       AND t.due_at < ?
     ORDER BY t.due_at ASC
     LIMIT 2000`,
    propertyId,
    from,
    to
  );
}

/**
 * Build the printable Annual Home Checklist task list — combines REAL
 * tasks in the year (completed + active rows from the live DB) with
 * SYNTHETIC tasks for any month/rule that has no live row. This way the
 * printout has something to do in every month, even months that already
 * passed before the user onboarded.
 *
 * De-dup key: (rule_key, month). When both a real and synthetic exist
 * for the same key+month, the real one wins because it carries the
 * user's actual completion state.
 *
 * Custom user-added tasks pass through unchanged via the live query.
 */
/**
 * Replan summary — what the diff between before / after a regen looks
 * like. Use this to populate the post-replan notice so the user always
 * knows what changed. Counts are over OPEN, non-custom tasks; titles
 * are capped to a small preview for display.
 */
export interface ReplanDiff {
  added: number;
  removed: number;
  rescheduled: number;
  addedTitles: string[];
  removedTitles: string[];
  /** Titles of tasks that shifted by > 24h. Surfaced in the replan
   *  modal as a third bulleted list so the user can see exactly which
   *  existing tasks moved to better dates, not just a count. */
  rescheduledTitles: string[];
}

export function formatReplanDiff(diff: ReplanDiff): string {
  const lines: string[] = [];
  if (diff.added > 0) {
    const preview = diff.addedTitles.slice(0, 2).join(', ');
    const more = diff.added > 2 ? ` (+${diff.added - 2} more)` : '';
    lines.push(
      `Added ${diff.added} task${diff.added === 1 ? '' : 's'}: ${preview}${more}.`
    );
  }
  if (diff.removed > 0) {
    const preview = diff.removedTitles.slice(0, 2).join(', ');
    const more = diff.removed > 2 ? ` (+${diff.removed - 2} more)` : '';
    lines.push(
      `Removed ${diff.removed} no-longer-needed task${diff.removed === 1 ? '' : 's'}: ${preview}${more}.`
    );
  }
  if (diff.rescheduled > 0) {
    lines.push(
      `Rescheduled ${diff.rescheduled} task${diff.rescheduled === 1 ? '' : 's'} to better dates.`
    );
  }
  if (lines.length === 0) {
    lines.push('Nothing changed — your plan was already up to date.');
  }
  return lines.join('\n\n');
}

/** How long a completion "covers" a rule before its task is due again.
 *  Shared by the regenerate path AND the replan preview — module scope
 *  so the two can never drift. */
const COMPLETION_DAY_MS = 86_400_000;
const TIER_WINDOW_MS: Record<string, number> = {
  weekly: 7 * COMPLETION_DAY_MS,
  monthly: 30 * COMPLETION_DAY_MS,
  quarterly: 90 * COMPLETION_DAY_MS,
  biannual: 180 * COMPLETION_DAY_MS,
  annual: 365 * COMPLETION_DAY_MS,
  'multi-year': 5 * 365 * COMPLETION_DAY_MS,
};

/**
 * Apply the completion-window adjustment to a freshly generated plan:
 * tasks whose (rule_key, system_id) was completed/skipped within the
 * rule's cadence window get their due date pushed to the end of that
 * window (or dropped in the defensive already-past case).
 *
 * This is THE canonical projection for a replan. Both
 * `regenerateTasksForProperty` (which inserts these rows) and
 * `previewReplanDiff` (which shows the user what will happen) must go
 * through it — Wave 49 fixed a bug where the preview diffed against
 * RAW plan dates and forever promised to "reschedule" a just-completed
 * task that the regen (correctly) kept parked at its pushed date.
 */
async function projectPlanWithCompletionWindows(
  db: SQLiteDatabase,
  propertyId: string,
  planTasks: PlannedTask[],
  nowMs: number
): Promise<Map<string, { task: PlannedTask; due_at: number }>> {
  const completions = await db.getAllAsync<{
    rule_key: string | null;
    system_id: string | null;
    last_done: number;
  }>(
    `SELECT rule_key, system_id, MAX(completed_at) as last_done FROM task
     WHERE property_id = ? AND completed_at IS NOT NULL
     GROUP BY rule_key, system_id`,
    propertyId
  );
  const completedAt = new Map<string, number>();
  for (const c of completions) {
    if (!c.rule_key) continue;
    completedAt.set(`${c.rule_key}::${c.system_id ?? ''}`, c.last_done);
  }
  const projected = new Map<string, { task: PlannedTask; due_at: number }>();
  for (const task of planTasks) {
    const key = `${task.rule_key}::${task.system_id ?? ''}`;
    const lastDone = completedAt.get(key);
    let due = task.due_at;
    if (lastDone != null) {
      const window = TIER_WINDOW_MS[task.tier] ?? 90 * COMPLETION_DAY_MS;
      const nextEligible = lastDone + window;
      // Planned due inside the covered window → push to the window's
      // end so the user sees the future occurrence without being
      // nagged about work they just did.
      if (due < nextEligible) due = nextEligible;
      // Defensive: just completed AND the pushed date is somehow still
      // in the past — drop rather than immediately resurface a copy.
      if (nowMs - lastDone < window && nextEligible <= nowMs) continue;
    }
    projected.set(key, { task, due_at: due });
  }
  return projected;
}

/**
 * Compute what a replan WOULD do — without actually doing it. Runs the
 * rule engine in memory, builds the projected plan, and diffs against
 * the current live tasks. Used by the Replan modal as a confirmation
 * step ("here's what will happen if you tap Replan now") rather than
 * the previous after-the-fact summary.
 *
 * Identity logic mirrors `regenerateTasksWithDiff` so the preview's
 * counts match what the actual regen produces — same (rule_key +
 * system_id) keying, same 24h threshold for rescheduled, same
 * completion-window projection. Excludes custom user-added tasks
 * (regen leaves those alone).
 */
export async function previewReplanDiff(
  db: SQLiteDatabase,
  propertyId: string
): Promise<ReplanDiff> {
  const property = await db.getFirstAsync<PropertyRow>(
    `SELECT * FROM property WHERE id = ? AND deleted_at IS NULL`,
    propertyId
  );
  if (!property) {
    return { added: 0, removed: 0, rescheduled: 0, addedTitles: [], removedTitles: [], rescheduledTitles: [] };
  }
  const systems = await getSystemsForProperty(db, propertyId);
  const plan = generatePlanFromSystems({
    homeType: property.home_type,
    hvacType: property.hvac_type,
    secondaryHvacType: property.secondary_hvac_type ?? null,
    climateZone: property.climate_zone,
    lat: property.lat,
    systems,
    basement: property.basement,
    waterSource: property.water_source,
    sewer: property.sewer,
    roofMaterial: property.roof_material,
    foundation: property.foundation,
    roofMaterials: getRoofMaterials(property),
    foundations: getFoundations(property),
    exteriorMaterials: getExteriorMaterials(property),
    yearBand: property.year_band,
    hasPool: property.has_pool === 1,
    hasSolar: property.has_solar === 1,
    hasPets: property.has_pets === 1,
    hasKids: property.has_kids === 1,
    hasHoa: property.has_hoa === 1,
    hasWoodfire: property.has_woodfire === 1,
    hasSmartHome: property.has_smart_home === 1,
    hasStrHost: property.has_str_host === 1,
    hasHeavySnow: property.has_heavy_snow === 1,
    hasGarden: property.has_garden === 1,
    hasAccessibility: property.has_accessibility === 1,
    isFirstTime: property.is_first_time === 1,
    travelsOften: property.travels_often === 1,
    worksFromHome: property.works_from_home === 1,
    hasAllergies: property.has_allergies === 1,
    hasNewborn: property.has_newborn === 1,
    multiGenHousehold: property.multi_gen_household === 1,
    over60Owner: property.over_60_owner === 1,
    hasLivestock: property.has_livestock === 1,
    hasShortTermContractors: property.has_short_term_contractors === 1,
    regionHazards: parseRegionHazards(property.region_hazards_json),
    hemisphere: (property.hemisphere === 'southern' ? 'southern' : 'northern'),
    monthlyMode: property.monthly_task_mode === 1,
  });

  type Snap = {
    title: string | null;
    rule_key: string | null;
    system_id: string | null;
    due_at: number | null;
  };
  const liveRows = await db.getAllAsync<Snap>(
    `SELECT title, rule_key, system_id, due_at FROM task
     WHERE property_id = ? AND completed_at IS NULL AND deleted_at IS NULL
       AND is_custom = 0`,
    propertyId
  );
  const live = new Map<string, Snap>();
  for (const r of liveRows) {
    if (!r.rule_key) continue;
    live.set(`${r.rule_key}::${r.system_id ?? ''}`, r);
  }
  // Project with completion windows applied — the SAME dates the
  // regenerate path will actually write, so the preview never lists a
  // "reschedule" the regen won't perform.
  const adjusted = await projectPlanWithCompletionWindows(
    db,
    propertyId,
    plan.tasks,
    Date.now()
  );
  const projected = new Map<string, { title: string; due_at: number }>();
  for (const [key, v] of adjusted) {
    if (!v.task.rule_key) continue;
    projected.set(key, { title: v.task.title, due_at: v.due_at });
  }
  const addedTitles: string[] = [];
  const rescheduledTitles: string[] = [];
  const DAY_MS = 24 * 60 * 60 * 1000;
  for (const [key, p] of projected) {
    const liveRow = live.get(key);
    if (!liveRow) {
      addedTitles.push(p.title);
    } else {
      const prevDue = liveRow.due_at ?? 0;
      if (Math.abs(p.due_at - prevDue) > DAY_MS) {
        rescheduledTitles.push(p.title);
      }
    }
  }
  const removedTitles: string[] = [];
  for (const [key, r] of live) {
    if (!projected.has(key) && r.title) removedTitles.push(r.title);
  }
  // When the diff is empty AND needs_replan was on, the user
  // already-implicitly resolved the flag (their edit didn't actually
  // change task scheduling, or they re-edited back to a synced state).
  // Auto-clear so the Replan badge disappears instead of forcing the
  // user to confirm a no-op regenerate. Without this, the badge sticks
  // and users wonder why the prompt keeps saying "Nothing to update".
  if (
    addedTitles.length === 0 &&
    removedTitles.length === 0 &&
    rescheduledTitles.length === 0 &&
    property.needs_replan === 1
  ) {
    await db.runAsync(
      `UPDATE property SET needs_replan = 0, updated_at = ? WHERE id = ?`,
      Date.now(),
      propertyId
    );
    // Tell badge subscribers (Replan chip, toolbar tint) right away.
    bumpDataRevision();
  }
  return {
    added: addedTitles.length,
    removed: removedTitles.length,
    rescheduled: rescheduledTitles.length,
    addedTitles,
    removedTitles,
    rescheduledTitles,
  };
}

/**
 * Regenerate this property's plan AND return a diff describing what
 * changed. Use this everywhere a replan happens (My Home button, Edit
 * Home Replan button, post-save auto-replan) so the user always sees
 * a concrete summary instead of an opaque "Plan refreshed."
 *
 * The diff identity is (rule_key + system_id) because the regen
 * rewrites task ids — rule_key is the only stable handle across runs.
 */
export async function regenerateTasksWithDiff(
  db: SQLiteDatabase,
  propertyId: string
): Promise<ReplanDiff> {
  type Snap = {
    title: string | null;
    rule_key: string | null;
    system_id: string | null;
    due_at: number | null;
  };
  const beforeRows = await db.getAllAsync<Snap>(
    `SELECT title, rule_key, system_id, due_at FROM task
     WHERE property_id = ? AND completed_at IS NULL AND deleted_at IS NULL
       AND is_custom = 0`,
    propertyId
  );
  const before = new Map<string, Snap>();
  for (const r of beforeRows) {
    if (!r.rule_key) continue;
    before.set(`${r.rule_key}::${r.system_id ?? ''}`, r);
  }
  await regenerateTasksForProperty(db, propertyId);
  const afterRows = await db.getAllAsync<Snap>(
    `SELECT title, rule_key, system_id, due_at FROM task
     WHERE property_id = ? AND completed_at IS NULL AND deleted_at IS NULL
       AND is_custom = 0`,
    propertyId
  );
  const after = new Map<string, Snap>();
  for (const r of afterRows) {
    if (!r.rule_key) continue;
    after.set(`${r.rule_key}::${r.system_id ?? ''}`, r);
  }
  const addedTitles: string[] = [];
  const rescheduledTitles: string[] = [];
  // 24-hour threshold: short-cycle tiers (weekly/monthly/quarterly)
  // recalculate as `today + N days`, so milliseconds of clock drift
  // would otherwise mark every one of them as "rescheduled" on every
  // regen. The catalog has ~63 such tasks per home — the old diff
  // proudly reported "63 tasks rescheduled" after a completely no-op
  // replan. A real date change is at minimum a one-day shift, so
  // anything finer is just clock noise we shouldn't surface.
  const DAY_MS = 24 * 60 * 60 * 1000;
  for (const [key, row] of after) {
    const prev = before.get(key);
    if (!prev) {
      if (row.title) addedTitles.push(row.title);
    } else {
      const prevDue = prev.due_at ?? 0;
      const nextDue = row.due_at ?? 0;
      if (Math.abs(nextDue - prevDue) > DAY_MS && row.title) {
        rescheduledTitles.push(row.title);
      }
    }
  }
  const removedTitles: string[] = [];
  for (const [key, row] of before) {
    if (!after.has(key) && row.title) removedTitles.push(row.title);
  }
  // Clear the dirty flag — the user just replanned, so the schedule
  // and the home profile are back in sync. Bump AFTER the clear so
  // badge subscribers re-read the flag's new value (the regen's own
  // bump can land before this write commits).
  await db.runAsync(
    `UPDATE property SET needs_replan = 0, updated_at = ? WHERE id = ?`,
    now(),
    propertyId
  );
  bumpDataRevision();
  return {
    added: addedTitles.length,
    removed: removedTitles.length,
    rescheduled: rescheduledTitles.length,
    addedTitles,
    removedTitles,
    rescheduledTitles,
  };
}

/**
 * Build the printable Annual Home Checklist task list — a pure
 * SYNTHETIC projection of every applicable rule across the calendar
 * year, PLUS any custom user-added tasks for that year. We intentionally
 * do NOT merge regen-derived live tasks because:
 *   - they include "overdue" rows the user explicitly didn't want on
 *     the printable calendar, and
 *   - their due_at can drift away from the natural season anchor
 *     (regen schedules forward from today, so a spring task in November
 *     prints next year's date).
 * The synthetic projection puts every rule in its canonical month for
 * the requested year, which is what makes a wall calendar useful.
 */
export async function getAnnualChecklistTasks(
  db: SQLiteDatabase,
  propertyId: string,
  year: number
): Promise<ActiveTask[]> {
  const property = await db.getFirstAsync<PropertyRow>(
    `SELECT * FROM property WHERE id = ? AND deleted_at IS NULL`,
    propertyId
  );
  if (!property) return [];
  // Pull custom user-added tasks for the year so personal additions
  // still print alongside the projection.
  const fromYear = new Date(year, 0, 1).getTime();
  const toYear = new Date(year + 1, 0, 1).getTime();
  const customTasks = await db.getAllAsync<ActiveTask>(
    `${ACTIVE_TASK_SELECT}
     WHERE t.property_id = ?
       AND t.deleted_at IS NULL
       AND t.is_custom = 1
       AND t.due_at >= ?
       AND t.due_at < ?
     ORDER BY t.due_at ASC`,
    propertyId,
    fromYear,
    toYear
  );
  const systems = await getSystemsForProperty(db, propertyId);
  const projected = projectAnnualPlan(
    {
      homeType: property.home_type,
      hvacType: property.hvac_type,
      secondaryHvacType: property.secondary_hvac_type ?? null,
      climateZone: property.climate_zone,
      lat: property.lat,
      systems,
      basement: property.basement,
      waterSource: property.water_source,
      sewer: property.sewer,
      roofMaterial: property.roof_material,
      foundation: property.foundation,
      roofMaterials: getRoofMaterials(property),
      foundations: getFoundations(property),
      exteriorMaterials: getExteriorMaterials(property),
      yearBand: property.year_band,
      hasPool: property.has_pool === 1,
      hasSolar: property.has_solar === 1,
      hasPets: property.has_pets === 1,
      hasKids: property.has_kids === 1,
      hasHoa: property.has_hoa === 1,
      hasWoodfire: property.has_woodfire === 1,
      hasSmartHome: property.has_smart_home === 1,
      hasStrHost: property.has_str_host === 1,
      hasHeavySnow: property.has_heavy_snow === 1,
      hasGarden: property.has_garden === 1,
      hasAccessibility: property.has_accessibility === 1,
      isFirstTime: property.is_first_time === 1,
      travelsOften: property.travels_often === 1,
      worksFromHome: property.works_from_home === 1,
      hasAllergies: property.has_allergies === 1,
      hasNewborn: property.has_newborn === 1,
      multiGenHousehold: property.multi_gen_household === 1,
      over60Owner: property.over_60_owner === 1,
      hasLivestock: property.has_livestock === 1,
      hasShortTermContractors: property.has_short_term_contractors === 1,
      regionHazards: parseRegionHazards(property.region_hazards_json),
      hemisphere: (property.hemisphere === 'southern' ? 'southern' : 'northern'),
    } as Parameters<typeof projectAnnualPlan>[0],
    year
  );

  // Cast projected rules into ActiveTask shape so the PDF renderer can
  // treat them uniformly with custom tasks.
  const syntheticTasks: ActiveTask[] = projected.map((p, idx) => ({
    id: `__projected__${p.rule_key}__${idx}`,
    property_id: propertyId,
    system_id: p.system_id ?? null,
    rule_key: p.rule_key ?? null,
    title: p.title,
    why: p.why ?? null,
    icon: p.icon ?? null,
    tier: p.tier,
    season: p.season,
    category: p.category,
    time_minutes: p.time_minutes ?? null,
    difficulty: p.difficulty ?? null,
    supplies_json: null,
    steps_json: null,
    tips_json: null,
    due_at: p.due_at,
    completed_at: null,
    snoozed_until: null,
    vendor_id: null,
    cost_cents: null,
    receipt_uri: null,
    notes: null,
    is_custom: 0,
    created_at: now(),
    updated_at: now(),
    deleted_at: null,
    reminder_offset_minutes: null,
    reminder_notification_id: null,
    system_kind: p.system_kind ?? null,
    system_subtype: null,
    system_label: null,
  }) as unknown as ActiveTask);

  return [...customTasks, ...syntheticTasks].sort(
    (a, b) => (a.due_at ?? 0) - (b.due_at ?? 0)
  );
}

export async function getActiveTasks(
  db: SQLiteDatabase,
  propertyId: string,
  limit = 60
): Promise<ActiveTask[]> {
  return await db.getAllAsync<ActiveTask>(
    `${ACTIVE_TASK_SELECT}
     WHERE t.property_id = ?
       AND t.completed_at IS NULL
       AND t.deleted_at IS NULL
       AND (t.snoozed_until IS NULL OR t.snoozed_until <= ?)
     ORDER BY t.due_at ASC
     LIMIT ?`,
    propertyId,
    now(),
    limit
  );
}

export async function getTaskById(
  db: SQLiteDatabase,
  id: string
): Promise<ActiveTask | null> {
  return await db.getFirstAsync<ActiveTask>(
    `${ACTIVE_TASK_SELECT} WHERE t.id = ? AND t.deleted_at IS NULL`,
    id
  );
}

export async function getSystemsForProperty(
  db: SQLiteDatabase,
  propertyId: string
): Promise<SystemRow[]> {
  return await db.getAllAsync<SystemRow>(
    `SELECT * FROM system
     WHERE property_id = ? AND deleted_at IS NULL
     ORDER BY location, kind, created_at`,
    propertyId
  );
}

export async function getSystemById(
  db: SQLiteDatabase,
  id: string
): Promise<SystemRow | null> {
  return await db.getFirstAsync<SystemRow>(
    `SELECT * FROM system WHERE id = ? AND deleted_at IS NULL`,
    id
  );
}

export interface NewSystemInput {
  propertyId: string;
  kind: string;
  location: SystemLocation;
  subtype?: string | null;
  nickname?: string | null;
  model?: string | null;
  filter_size?: string | null;
  installed_year?: number | null;
  photo_uri?: string | null;
  notes?: string | null;
  is_active?: number;
  active_start_month?: number | null;
  active_end_month?: number | null;
  custom_label?: string | null;
  custom_icon?: string | null;
  custom_category?: string | null;
}

export interface NewCustomSystemInput {
  propertyId: string;
  label: string;
  icon: string;
  location: SystemLocation;
  category: string;
}

/**
 * Convenience helper for the "Create a custom system" path. Generates a
 * unique `custom:<id>` kind, stamps the custom_* columns so display logic
 * can render it, and inserts a fresh row.
 */
export async function addCustomSystem(
  db: SQLiteDatabase,
  input: NewCustomSystemInput
): Promise<string> {
  const id = newId();
  const kind = `custom:${id.slice(0, 8)}`;
  return addSystem(db, {
    propertyId: input.propertyId,
    kind,
    location: input.location,
    nickname: input.label,
    custom_label: input.label,
    custom_icon: input.icon,
    custom_category: input.category,
  });
}

export async function addSystem(
  db: SQLiteDatabase,
  input: NewSystemInput
): Promise<string> {
  const id = newId();
  const t = now();
  await db.runAsync(
    `INSERT INTO system (
      id, property_id, kind, subtype, nickname, location,
      model, filter_size, installed_year, photo_uri, notes,
      is_active, active_start_month, active_end_month,
      custom_label, custom_icon, custom_category,
      created_at, updated_at
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    id,
    input.propertyId,
    input.kind,
    input.subtype ?? null,
    input.nickname ?? null,
    input.location,
    input.model ?? null,
    input.filter_size ?? null,
    input.installed_year ?? null,
    input.photo_uri ?? null,
    input.notes ?? null,
    input.is_active ?? 1,
    input.active_start_month ?? null,
    input.active_end_month ?? null,
    input.custom_label ?? null,
    input.custom_icon ?? null,
    input.custom_category ?? null,
    t,
    t
  );
  return id;
}

export interface UpdateSystemInput {
  subtype?: string | null;
  nickname?: string | null;
  model?: string | null;
  filter_size?: string | null;
  installed_year?: number | null;
  photo_uri?: string | null;
  notes?: string | null;
  is_active?: number;
  active_start_month?: number | null;
  active_end_month?: number | null;
  /** Wave 43: timestamp when the user confirmed the seasonal window
   *  in the Tune walkthrough. Stamped on commit. */
  seasonal_confirmed_at?: number | null;
  /** Multi-photo gallery — array of file URIs, persisted as JSON. */
  extra_photos?: string[] | null;
  /** Standalone reminder timestamp (renewal date, warranty expiry, etc.). */
  reminder_at?: number | null;
  reminder_label?: string | null;
  /** Schema v36/v37 — Locations module. Photo + short WHERE-in-the-home
   *  label + how-to-operate one-liner. Drives the Emergency Card. */
  location_photo_uri?: string | null;
  location_where?: string | null;
  location_note?: string | null;
}

export async function updateSystem(
  db: SQLiteDatabase,
  id: string,
  input: UpdateSystemInput
): Promise<void> {
  const t = now();
  const sets: string[] = [];
  const args: (string | number | null)[] = [];
  const fields: (keyof UpdateSystemInput)[] = [
    'subtype', 'nickname', 'model', 'filter_size', 'installed_year',
    'photo_uri', 'notes', 'is_active', 'active_start_month', 'active_end_month',
    'seasonal_confirmed_at',
    'reminder_at', 'reminder_label',
    'location_photo_uri', 'location_where', 'location_note',
  ];
  for (const f of fields) {
    if (input[f] !== undefined) {
      sets.push(`${f} = ?`);
      args.push(input[f] as string | number | null);
    }
  }
  if (input.extra_photos !== undefined) {
    sets.push(`extra_photos_json = ?`);
    args.push(
      input.extra_photos && input.extra_photos.length
        ? JSON.stringify(input.extra_photos)
        : null
    );
  }
  if (sets.length === 0) return;
  sets.push('updated_at = ?');
  args.push(t);
  args.push(id);
  await db.runAsync(`UPDATE system SET ${sets.join(', ')} WHERE id = ?`, ...args);
  // Tell subscribers — Home ring, AppHeader, the weekly-prompt sweep —
  // that data has changed. Without this, picking a subtype in the
  // walkthrough wouldn't trigger the inbox sweep that auto-dismisses
  // the prompt the user just resolved.
  bumpDataRevision();
}

export async function deleteSystem(db: SQLiteDatabase, id: string): Promise<void> {
  const t = now();
  await db.runAsync(
    `UPDATE system SET deleted_at = ?, updated_at = ? WHERE id = ?`,
    t,
    t,
    id
  );
}

/**
 * History view: tasks the user has actually finished. Ordered most-recent
 * first. Used by the Completed-tasks screen so you can scroll your past
 * work, see the cost/vendor breakdown, etc.
 */
export async function getCompletedTasks(
  db: SQLiteDatabase,
  propertyId: string,
  limit = 200
): Promise<ActiveTask[]> {
  // Subquery picks the most-recent task_completion row's `kind` so the
  // list view can tag each entry as done vs skipped. Per-row subquery is
  // cheap with the existing idx_completion_task index — the list caps
  // at 500 rows so we're not blowing through it.
  return await db.getAllAsync<ActiveTask>(
    `SELECT t.*,
       s.kind AS system_kind,
       s.subtype AS system_subtype,
       s.nickname AS system_nickname,
       (SELECT kind FROM task_completion
         WHERE task_id = t.id
         ORDER BY completed_at DESC
         LIMIT 1) AS completion_kind
     FROM task t
     LEFT JOIN system s ON s.id = t.system_id
     WHERE t.property_id = ?
       AND t.completed_at IS NOT NULL
       AND t.deleted_at IS NULL
     ORDER BY t.completed_at DESC
     LIMIT ?`,
    propertyId,
    limit
  );
}

/**
 * Current consecutive-day completion streak. Walks back from today
 * counting days that had at least one `task_completion` (any kind),
 * stops on the first gap. Skips count — the user closed a task that
 * day, even if they didn't actually do it; rewarding showing up is the
 * gamification we want.
 */
export async function getStreakDays(
  db: SQLiteDatabase,
  propertyId: string
): Promise<number> {
  // Pull all completion timestamps for this property; bucket to local
  // YYYY-MM-DD; walk back from today.
  const rows = await db.getAllAsync<{ ts: number }>(
    `SELECT tc.completed_at AS ts
     FROM task_completion tc
     JOIN task t ON t.id = tc.task_id
     WHERE t.property_id = ? AND t.deleted_at IS NULL
     ORDER BY tc.completed_at DESC
     LIMIT 1000`,
    propertyId
  );
  if (rows.length === 0) return 0;
  const days = new Set<string>();
  for (const r of rows) {
    const d = new Date(r.ts);
    days.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
  }
  let streak = 0;
  const cursor = new Date();
  // Allow the streak to count today only if there's a completion today
  // OR (no completion today but one yesterday — treat today as still in
  // progress). Pragma: don't break the streak just because the day
  // isn't over yet. We check today's key first; if missing, start from
  // yesterday for the walk-back.
  const todayKey = `${cursor.getFullYear()}-${cursor.getMonth()}-${cursor.getDate()}`;
  if (!days.has(todayKey)) {
    cursor.setDate(cursor.getDate() - 1);
  }
  while (true) {
    const key = `${cursor.getFullYear()}-${cursor.getMonth()}-${cursor.getDate()}`;
    if (!days.has(key)) break;
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

export async function getDoneCountThisWeek(
  db: SQLiteDatabase,
  propertyId: string
): Promise<number> {
  const start = now() - WEEK_MS;
  const row = await db.getFirstAsync<{ n: number }>(
    `SELECT COUNT(*) AS n FROM task
     WHERE property_id = ? AND completed_at >= ? AND deleted_at IS NULL`,
    propertyId,
    start
  );
  return row?.n ?? 0;
}

export interface SeasonProgress {
  /** Current season name, derived from today's date. */
  season: 'Spring' | 'Summer' | 'Fall' | 'Winter';
  /** Tasks completed in the current season window. */
  completed: number;
  /** Total tasks scheduled in the current season window (completed + outstanding). */
  total: number;
  /** Percent 0–100, completed / max(total, 1) * 100, rounded. 0 on a fresh setup. */
  pct: number;
}

/**
 * "How am I doing on this season's chores?" — completion-based progress so
 * a fresh setup starts at 0% and climbs as you finish tasks. Counts tasks
 * whose `season` matches the current astronomical season (or 'any' tasks
 * due during the season window). A season has no fixed task count up front;
 * it grows as the planner adds rules so the denominator is `total within
 * the season window`, not a hardcoded ceiling.
 */
export type SeasonKey = 'spring' | 'summer' | 'fall' | 'winter';

export interface SeasonStat {
  done: number;
  /** Total tasks bucketed into this season (done + still-open). */
  total: number;
  pct: number;
}

/**
 * Per-season completion stats for all four seasons. ONE definition of
 * "what counts as a spring task" lives here so the Home season ring and
 * the Schedule season chips can never drift apart.
 *
 * Membership rule: `t.season === seasonKey` OR `t.due_at` falls within
 * that season's astronomical window. `season = 'any'` tasks bucket by
 * due_at window only (otherwise they'd inflate every season's counts).
 */
export async function getAllSeasonsProgress(
  db: SQLiteDatabase,
  propertyId: string
): Promise<Record<SeasonKey, SeasonStat>> {
  const y = new Date().getFullYear();
  const today = Date.now();
  // (Removed: isLateDec branch — the seasonWindow helper below handles
  // year-rollover for the wrap-around winter case uniformly.)
  // For each season, pick the CURRENT or NEXT-UPCOMING window so the
  // user always sees the season they're either in or about to enter.
  // Previously winter for a May user meant "Dec 2024 → Mar 2025" — all
  // in the past — so winter always read 0 because the regen schedules
  // forward only. Now we advance past windows by 1 year automatically.
  // For each season, pick the CURRENT or NEXT-UPCOMING window. If the
  // current calendar-year occurrence has fully passed, slide forward to
  // next year so the season is anchored on tasks the regen has actually
  // scheduled (which only points forward).
  const seasonWindow = (
    startMonth: number, startDay: number,
    endMonth: number, endDay: number,
    wrapsYear: boolean
  ): [number, number] => {
    // Build candidate window. Winter wraps Dec → Mar so its initial
    // candidate is [Dec 21 (y-1), Mar 20 y]; advances forward in
    // 1-year increments until end > today.
    let startYear = wrapsYear ? y - 1 : y;
    let endYear = y;
    let start = new Date(startYear, startMonth, startDay).getTime();
    let end = new Date(endYear, endMonth, endDay).getTime();
    while (end <= today) {
      startYear += 1;
      endYear += 1;
      start = new Date(startYear, startMonth, startDay).getTime();
      end = new Date(endYear, endMonth, endDay).getTime();
    }
    return [start, end];
  };
  const BOUNDS: Record<SeasonKey, [number, number]> = {
    spring: seasonWindow(2, 20, 5, 21, false),
    summer: seasonWindow(5, 21, 8, 22, false),
    fall:   seasonWindow(8, 22, 11, 21, false),
    winter: seasonWindow(11, 21, 2, 20, true),
  };

  const out: Record<SeasonKey, SeasonStat> = {
    spring: { done: 0, total: 0, pct: 0 },
    summer: { done: 0, total: 0, pct: 0 },
    fall:   { done: 0, total: 0, pct: 0 },
    winter: { done: 0, total: 0, pct: 0 },
  };

  for (const s of Object.keys(BOUNDS) as SeasonKey[]) {
    const [from, to] = BOUNDS[s];
    // Year-scoped queries. The previous version included
    // `season = ?` OR a date window, which over-counted because the
    // task generator produces multi-year-out tasks tagged with the
    // same season string ('spring' etc) regardless of year. A user
    // who'd completed every Spring 2027 task could see "Spring 65%"
    // because Spring 2028 tasks (generated, not done) were dragging
    // the ratio down. Restricting to the date window pins each
    // season's progress to THIS CALENDAR YEAR.
    const totalRow = await db.getFirstAsync<{ n: number }>(
      `SELECT COUNT(*) AS n FROM task
       WHERE property_id = ? AND deleted_at IS NULL
         AND due_at >= ? AND due_at < ?`,
      propertyId,
      from,
      to
    );
    const doneRow = await db.getFirstAsync<{ n: number }>(
      `SELECT COUNT(*) AS n FROM task
       WHERE property_id = ? AND deleted_at IS NULL
         AND completed_at IS NOT NULL
         AND due_at >= ? AND due_at < ?`,
      propertyId,
      from,
      to
    );
    out[s].total = totalRow?.n ?? 0;
    out[s].done = doneRow?.n ?? 0;
    out[s].pct = out[s].total > 0
      ? Math.round((out[s].done / out[s].total) * 100)
      : 0;
  }
  return out;
}

export async function getSeasonProgress(
  db: SQLiteDatabase,
  propertyId: string
): Promise<SeasonProgress> {
  const { name } = currentSeasonLabel(new Date());
  const stats = await getAllSeasonsProgress(db, propertyId);
  const s = name.toLowerCase() as SeasonKey;
  return {
    season: name,
    completed: stats[s].done,
    total: stats[s].total,
    pct: stats[s].pct,
  };
}

/**
 * System kinds that have subtype options defined in SYSTEM_CATALOG.
 * Derived at runtime from the catalog so this can't drift — every new
 * system with a non-empty `subtypes` array automatically counts toward
 * the profile-ring subtype-coverage signal, the "Needs tune · N" chip
 * on Home, and the Systems page filter.
 */
const KINDS_WITH_SUBTYPES: readonly string[] = Object.entries(SYSTEM_CATALOG)
  .filter(([, meta]) => Array.isArray(meta?.subtypes) && meta.subtypes.length > 0)
  .map(([key]) => key);

/**
 * Count how many of the user's systems are *eligible* for subtype
 * (the kind has subtype options) vs how many have actually had a
 * subtype selected. Both numbers go into the profile ring formula.
 */
/**
 * Wave 43: Count seasonal-eligible systems the user hasn't confirmed
 * yet in the Tune walkthrough. Used by the Tune badge so the number
 * shown matches what the walkthrough will actually surface (profile
 * blanks + subtype gaps + unconfirmed seasonal).
 *
 * Hardcoded SEASONAL_KIND list mirrors `src/data/seasonal.ts` — we
 * can't import that module here without a circular dependency, but
 * the kinds barely ever change.
 */
export async function getUnconfirmedSeasonalCount(
  db: SQLiteDatabase,
  propertyId: string
): Promise<number> {
  const SEASONAL_KIND_LIST = [
    'pool', 'pool_cover', 'irrig', 'sprinkler_controller', 'hose_bib',
    'central_ac', 'window_ac', 'garden', 'gas_grill',
  ];
  const placeholders = SEASONAL_KIND_LIST.map(() => '?').join(', ');
  const row = await db.getFirstAsync<{ n: number }>(
    `SELECT COUNT(*) AS n
     FROM system
     WHERE property_id = ? AND deleted_at IS NULL
       AND seasonal_confirmed_at IS NULL
       AND kind IN (${placeholders})`,
    propertyId,
    ...SEASONAL_KIND_LIST
  );
  return row?.n ?? 0;
}

export async function getSubtypeCoverageSignal(
  db: SQLiteDatabase,
  propertyId: string
): Promise<{ subtypeEligibleSystems: number; subtypesSelected: number }> {
  const placeholders = KINDS_WITH_SUBTYPES.map(() => '?').join(', ');
  const row = await db.getFirstAsync<{ eligible: number; selected: number }>(
    `SELECT
       COUNT(*) AS eligible,
       SUM(CASE WHEN subtype IS NOT NULL THEN 1 ELSE 0 END) AS selected
     FROM system
     WHERE property_id = ? AND deleted_at IS NULL
       AND kind IN (${placeholders})`,
    propertyId,
    ...KINDS_WITH_SUBTYPES
  );
  return {
    subtypeEligibleSystems: row?.eligible ?? 0,
    subtypesSelected: row?.selected ?? 0,
  };
}

/**
 * Kept for legacy callers (HealthCard, etc.) — returns the old binary
 * "is this system filled at all" tally. Profile ring no longer uses
 * this; subtype coverage is the only system-side signal that counts
 * toward the ring now (see getSubtypeCoverageSignal above).
 */
export async function getSystemDetailCount(
  db: SQLiteDatabase,
  propertyId: string
): Promise<{ filled: number; total: number }> {
  const row = await db.getFirstAsync<{ filled: number; total: number }>(
    `SELECT
       SUM(CASE
             WHEN model IS NOT NULL OR filter_size IS NOT NULL
                  OR subtype IS NOT NULL OR installed_year IS NOT NULL
                  OR nickname IS NOT NULL
             THEN 1 ELSE 0 END) AS filled,
       COUNT(*) AS total
     FROM system
     WHERE property_id = ? AND deleted_at IS NULL`,
    propertyId
  );
  return { filled: row?.filled ?? 0, total: row?.total ?? 0 };
}

/**
 * True if the user has completed at least one task on this property.
 * One-time ratchet for the profile ring — once true, stays true even
 * if the task gets soft-deleted later.
 */
export async function hasEverCompletedTask(
  db: SQLiteDatabase,
  propertyId: string
): Promise<boolean> {
  const row = await db.getFirstAsync<{ n: number }>(
    `SELECT COUNT(*) AS n FROM task
     WHERE property_id = ? AND completed_at IS NOT NULL`,
    propertyId
  );
  return (row?.n ?? 0) > 0;
}

export interface CompleteTaskInput {
  cost_cents?: number | null;
  vendor?: string | null;
  contact_id?: string | null;
  notes?: string | null;
}

export type CompletionKind = 'done' | 'skip';

async function recordTaskCompletion(
  db: SQLiteDatabase,
  taskId: string,
  kind: CompletionKind,
  detail: CompleteTaskInput
): Promise<{ completedAt: number }> {
  const t = now();
  await db.withTransactionAsync(async () => {
    await db.runAsync(
      `UPDATE task SET completed_at = ?, updated_at = ? WHERE id = ?`,
      t,
      t,
      taskId
    );
    await db.runAsync(
      `INSERT INTO task_completion (
        id, task_id, completed_at, notes, cost_cents, vendor, contact_id,
        kind, created_at, updated_at
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      newId(),
      taskId,
      t,
      detail.notes ?? null,
      detail.cost_cents ?? null,
      detail.vendor ?? null,
      detail.contact_id ?? null,
      kind,
      t,
      t
    );
    if (detail.contact_id) {
      await db.runAsync(
        `UPDATE contact SET last_used_at = ?, updated_at = ? WHERE id = ?`,
        t,
        t,
        detail.contact_id
      );
    }
  });
  // For recurring custom tasks, plant the next occurrence outside the
  // transaction (separate row, won't conflict with the just-closed
  // completion). Skips trigger the same follow-up logic — skipping a
  // recurring task closes this instance but should still schedule the
  // next one; otherwise users would have to manually re-create them.
  await rescheduleCustomFollowUp(db, taskId).catch((err) => {
    console.warn('rescheduleCustomFollowUp failed', err);
  });
  // Disarm any OS reminder for the just-closed task — done centrally so
  // EVERY complete/skip surface (Today, Schedule, complete/[id], swipe
  // actions) gets it, not just the ones that remember to call it.
  void cancelTaskReminder(taskId).catch(() => {});
  await db.runAsync(
    `UPDATE task SET reminder_at = NULL, reminder_notified_at = NULL,
       updated_at = ? WHERE id = ?`,
    t,
    taskId
  ).catch(() => {});
  // Notify any caching surfaces (e.g. desktop sidebar) that data has
  // changed, so they refetch without waiting for navigation.
  bumpDataRevision();
  return { completedAt: t };
}

/** One prior outcome of a task's rule — powers the History section on
 *  the task detail page (Wave 64). */
export interface TaskHistoryEntry {
  completed_at: number;
  kind: CompletionKind;
  notes: string | null;
  cost_cents: number | null;
  vendor: string | null;
}

/**
 * Every recorded outcome of THIS task's rule on THIS property, newest
 * first. Occurrences of a recurring rule live on separate task rows
 * (the planner re-seeds rules; custom follow-ups clone rows), so we
 * match by rule_key OR exact title rather than task id — that gathers
 * the full multi-year record ("did I actually flush the water heater
 * last fall, or skip it?").
 */
export async function getTaskHistory(
  db: SQLiteDatabase,
  task: { property_id: string; rule_key: string | null; title: string },
  limit = 24
): Promise<TaskHistoryEntry[]> {
  return await db.getAllAsync<TaskHistoryEntry>(
    `SELECT tc.completed_at, tc.kind, tc.notes, tc.cost_cents, tc.vendor
     FROM task_completion tc
     JOIN task t ON t.id = tc.task_id
     WHERE t.property_id = ?
       AND (t.rule_key = ? OR t.title = ?)
     ORDER BY tc.completed_at DESC
     LIMIT ?`,
    task.property_id,
    task.rule_key ?? '',
    task.title,
    limit
  );
}

export async function completeTask(
  db: SQLiteDatabase,
  taskId: string,
  detail: CompleteTaskInput = {}
): Promise<{ completedAt: number }> {
  return recordTaskCompletion(db, taskId, 'done', detail);
}

/**
 * Skip a task — closes it (removes from active planner) and records it
 * as `kind='skip'` in task_completion. Distinct from `completeTask` so
 * the past-tasks / timeline views can color-code "intentionally not
 * done" differently from "did it." Recurring follow-ups still get
 * scheduled — skipping this occurrence doesn't punish the rule.
 */
export async function skipTask(
  db: SQLiteDatabase,
  taskId: string,
  notes?: string | null
): Promise<{ completedAt: number }> {
  return recordTaskCompletion(db, taskId, 'skip', { notes: notes ?? null });
}

/**
 * Soft-delete a task. The user can wipe both custom and seeded tasks
 * — seeded entries that get deleted simply won't be re-created by the
 * planner unless they explicitly Replan. Soft-delete (deleted_at) so
 * any related completion / expense history still resolves; the row is
 * just hidden from active queries.
 */
export async function deleteTask(db: SQLiteDatabase, taskId: string): Promise<void> {
  const t = now();
  await db.runAsync(
    `UPDATE task SET deleted_at = ?, updated_at = ? WHERE id = ?`,
    t,
    t,
    taskId
  );
  void cancelTaskReminder(taskId).catch(() => {});
  bumpDataRevision();
}

/** Reverse a soft delete — powers the Undo toast after a delete. */
export async function undeleteTask(db: SQLiteDatabase, taskId: string): Promise<void> {
  const t = now();
  await db.runAsync(
    `UPDATE task SET deleted_at = NULL, updated_at = ? WHERE id = ?`,
    t,
    taskId
  );
  bumpDataRevision();
}

/** In-memory snapshot of a hard-deleted task, held by the Undo toast
 *  for its 5-second window. Rows are raw column maps so restore can
 *  re-INSERT them verbatim without tracking the schema here. */
export interface HardDeleteSnapshot {
  task: Record<string, unknown>;
  completions: Record<string, unknown>[];
}

/**
 * PERMANENT delete — removes the task row AND its completion history.
 * Returns a snapshot so the caller can offer a short-lived Undo (the
 * rows live only in memory; once the toast expires they're gone for
 * good). The UI must still confirm loudly before calling this.
 */
export async function hardDeleteTask(
  db: SQLiteDatabase,
  taskId: string
): Promise<HardDeleteSnapshot | null> {
  const task = await db.getFirstAsync<Record<string, unknown>>(
    `SELECT * FROM task WHERE id = ?`,
    taskId
  );
  const completions = await db.getAllAsync<Record<string, unknown>>(
    `SELECT * FROM task_completion WHERE task_id = ?`,
    taskId
  );
  await db.withTransactionAsync(async () => {
    await db.runAsync(`DELETE FROM task_completion WHERE task_id = ?`, taskId);
    await db.runAsync(`DELETE FROM task WHERE id = ?`, taskId);
  });
  void cancelTaskReminder(taskId).catch(() => {});
  bumpDataRevision();
  return task ? { task, completions: completions ?? [] } : null;
}

/** Re-insert a hard-deleted task + its completions — the Undo half of
 *  hardDeleteTask. Verbatim column-map INSERTs. */
export async function restoreHardDeletedTask(
  db: SQLiteDatabase,
  snap: HardDeleteSnapshot
): Promise<void> {
  const insert = async (table: string, row: Record<string, unknown>) => {
    const entries = Object.entries(row);
    const cols = entries.map(([k]) => k).join(', ');
    const placeholders = entries.map(() => '?').join(', ');
    const values = entries.map(([, v]) =>
      v === null || typeof v === 'number' || typeof v === 'string'
        ? (v as string | number | null)
        : String(v)
    );
    await db.runAsync(
      `INSERT OR REPLACE INTO ${table} (${cols}) VALUES (${placeholders})`,
      ...values
    );
  };
  await db.withTransactionAsync(async () => {
    await insert('task', snap.task);
    for (const c of snap.completions) await insert('task_completion', c);
  });
  bumpDataRevision();
}

/**
 * Edit a user-facing task field — title, time estimate, difficulty —
 * on an existing row. Also flips `is_custom = 1` so future Replans
 * preserve the edit (the regenerate path only rewrites is_custom=0
 * rows). To revert to the rule's original copy, delete the task and
 * let the next Replan recreate it from the rule.
 *
 * Difficulty accepts our standard enum; null clears the override.
 * Time minutes null clears the override. Title is required.
 */
export async function editTaskOverride(
  db: SQLiteDatabase,
  taskId: string,
  patch: {
    title?: string;
    time_minutes?: number | null;
    difficulty?: Difficulty | null;
  }
): Promise<void> {
  const sets: string[] = [];
  const args: (string | number | null)[] = [];
  if (patch.title !== undefined) {
    sets.push('title = ?');
    args.push(patch.title);
  }
  if (patch.time_minutes !== undefined) {
    sets.push('time_minutes = ?');
    args.push(patch.time_minutes);
  }
  if (patch.difficulty !== undefined) {
    sets.push('difficulty = ?');
    args.push(patch.difficulty);
  }
  if (sets.length === 0) return;
  // Flip is_custom so the regen path treats this row as user-owned
  // and won't blow it away on the next Replan.
  sets.push('is_custom = 1');
  sets.push('updated_at = ?');
  args.push(now());
  args.push(taskId);
  await db.runAsync(
    `UPDATE task SET ${sets.join(', ')} WHERE id = ?`,
    ...args
  );
  bumpDataRevision();
}

/**
 * Reverse a completion — restores the task to active so it shows up in the
 * planner again. Strips the most recent task_completion row (so service
 * history doesn't double-count and the cost rolls back out of dashboards)
 * and clears `completed_at` on the parent task. Leaves due_at untouched
 * because that was the date the user originally meant to do it; if it was
 * overdue when completed, it'll reappear as overdue.
 *
 * Used by the "Mark not done" button on past-task / completed-task views
 * when the user clicks one accidentally or wants to re-do it.
 */
export async function uncompleteTask(
  db: SQLiteDatabase,
  taskId: string
): Promise<void> {
  const t = now();
  await db.withTransactionAsync(async () => {
    await db.runAsync(
      `DELETE FROM task_completion
        WHERE id = (
          SELECT id FROM task_completion
          WHERE task_id = ?
          ORDER BY completed_at DESC
          LIMIT 1
        )`,
      taskId
    );
    await db.runAsync(
      `UPDATE task SET completed_at = NULL, updated_at = ? WHERE id = ?`,
      t,
      taskId
    );
  });
  bumpDataRevision();
}

export interface ServiceHistoryEntry {
  completion_id: string;
  task_id: string;
  task_title: string;
  rule_key: string;
  icon: string | null;
  completed_at: number;
  cost_cents: number | null;
  vendor: string | null;
  contact_name: string | null;
  notes: string | null;
}

export async function getServiceHistoryForSystem(
  db: SQLiteDatabase,
  systemId: string,
  limit = 50
): Promise<ServiceHistoryEntry[]> {
  return await db.getAllAsync<ServiceHistoryEntry>(
    `SELECT
       tc.id AS completion_id,
       t.id AS task_id,
       t.title AS task_title,
       t.rule_key AS rule_key,
       t.icon AS icon,
       tc.completed_at AS completed_at,
       tc.cost_cents AS cost_cents,
       tc.vendor AS vendor,
       c.name AS contact_name,
       tc.notes AS notes
     FROM task_completion tc
     JOIN task t ON t.id = tc.task_id
     LEFT JOIN contact c ON c.id = tc.contact_id
     WHERE t.system_id = ? AND t.deleted_at IS NULL
     ORDER BY tc.completed_at DESC
     LIMIT ?`,
    systemId,
    limit
  );
}

export async function getServiceHistoryForProperty(
  db: SQLiteDatabase,
  propertyId: string,
  limit = 100
): Promise<ServiceHistoryEntry[]> {
  return await db.getAllAsync<ServiceHistoryEntry>(
    `SELECT
       tc.id AS completion_id,
       t.id AS task_id,
       t.title AS task_title,
       t.rule_key AS rule_key,
       t.icon AS icon,
       tc.completed_at AS completed_at,
       tc.cost_cents AS cost_cents,
       tc.vendor AS vendor,
       c.name AS contact_name,
       tc.notes AS notes
     FROM task_completion tc
     JOIN task t ON t.id = tc.task_id
     LEFT JOIN contact c ON c.id = tc.contact_id
     WHERE t.property_id = ? AND t.deleted_at IS NULL
     ORDER BY tc.completed_at DESC
     LIMIT ?`,
    propertyId,
    limit
  );
}

export async function getYearToDateSpend(
  db: SQLiteDatabase,
  propertyId: string
): Promise<number> {
  const yearStart = new Date(new Date().getFullYear(), 0, 1).getTime();
  const row = await db.getFirstAsync<{ total: number | null }>(
    `SELECT SUM(tc.cost_cents) AS total
     FROM task_completion tc
     JOIN task t ON t.id = tc.task_id
     WHERE t.property_id = ? AND t.deleted_at IS NULL AND tc.completed_at >= ?`,
    propertyId,
    yearStart
  );
  return row?.total ?? 0;
}

/**
 * Reschedule a task to N days from now. Moves due_at and clears the
 * snoozed_until "hide" marker so the task remains visible in the lists —
 * just in a later partition. The smart hero selection in `partitionTasks`
 * keeps it out of the Your-One-Thing slot so the user sees the reschedule
 * actually advance the hero card.
 *
 * `snoozeTask` is kept as an alias for the existing call sites — same
 * behavior under the new name.
 */
export async function rescheduleTask(
  db: SQLiteDatabase,
  taskId: string,
  days = 7
): Promise<{ prevDueAt: number | null }> {
  const prev = await db.getFirstAsync<{ due_at: number | null }>(
    `SELECT due_at FROM task WHERE id = ?`,
    taskId
  );
  const t = now();
  // Anchor to NOON of the target day, not now+N*24h. With the raw offset,
  // "Tomorrow" computed at 11pm becomes 11pm tomorrow, which still lives
  // inside today's calendar day in some edge cases and surfaces as
  // "Due today" in dueLabel. Noon-of-day is robust + reads natural to the
  // user ("see me again tomorrow").
  const target = new Date(t);
  target.setDate(target.getDate() + days);
  target.setHours(12, 0, 0, 0);
  const newDueAt = target.getTime();
  // Clear the reminder alongside the move: the OS one-shot (native) and
  // the reminder_at row fields (web focus-checker) both point at the OLD
  // due date. Leaving them armed either pings at the wrong time or — on
  // web — fires "overdue" the moment the app regains focus.
  await db.runAsync(
    `UPDATE task
       SET due_at = ?, snoozed_until = NULL,
           reminder_at = NULL, reminder_lead_days = NULL,
           reminder_notified_at = NULL, updated_at = ?
     WHERE id = ?`,
    newDueAt,
    t,
    taskId
  );
  void cancelTaskReminder(taskId).catch(() => {});
  bumpDataRevision();
  return { prevDueAt: prev?.due_at ?? null };
}

export const snoozeTask = rescheduleTask;

/** Restore a task's due date — the Undo half of rescheduleTask. */
export async function setTaskDueAt(
  db: SQLiteDatabase,
  taskId: string,
  dueAt: number | null
): Promise<void> {
  await db.runAsync(
    `UPDATE task SET due_at = ?, updated_at = ? WHERE id = ?`,
    dueAt,
    now(),
    taskId
  );
  bumpDataRevision();
}

/**
 * Drops every pending (uncompleted, undeleted) task on the property and
 * rebuilds them from the current system rows. Run after a system is added,
 * its subtype is edited, or it's deleted, so the plan stays in sync.
 *
 * Completed and explicitly snoozed tasks are preserved.
 */
export async function regenerateTasksForProperty(
  db: SQLiteDatabase,
  propertyId: string
): Promise<number> {
  const property = await db.getFirstAsync<PropertyRow>(
    `SELECT * FROM property WHERE id = ? AND deleted_at IS NULL`,
    propertyId
  );
  if (!property) return 0;
  const systems = await getSystemsForProperty(db, propertyId);
  const plan = generatePlanFromSystems({
    homeType: property.home_type,
    hvacType: property.hvac_type,
    secondaryHvacType: property.secondary_hvac_type ?? null,
    climateZone: property.climate_zone,
    lat: property.lat,
    systems,
    basement: property.basement,
    waterSource: property.water_source,
    sewer: property.sewer,
    roofMaterial: property.roof_material,
    foundation: property.foundation,
    // Multi-value v32: prefer the arrays — they're the canonical
    // source post-migration. Helpers below parse the JSON and fall
    // back to the legacy single-value if no array is set.
    roofMaterials: getRoofMaterials(property),
    foundations: getFoundations(property),
    exteriorMaterials: getExteriorMaterials(property),
    yearBand: property.year_band,
    hasPool: property.has_pool === 1,
    hasSolar: property.has_solar === 1,
    hasPets: property.has_pets === 1,
    hasKids: property.has_kids === 1,
    hasHoa: property.has_hoa === 1,
    hasWoodfire: property.has_woodfire === 1,
    hasSmartHome: property.has_smart_home === 1,
    hasStrHost: property.has_str_host === 1,
    hasHeavySnow: property.has_heavy_snow === 1,
    hasGarden: property.has_garden === 1,
    hasAccessibility: property.has_accessibility === 1,
    isFirstTime: property.is_first_time === 1,
    // v35 lifestyle additions.
    travelsOften: property.travels_often === 1,
    worksFromHome: property.works_from_home === 1,
    hasAllergies: property.has_allergies === 1,
    hasNewborn: property.has_newborn === 1,
    multiGenHousehold: property.multi_gen_household === 1,
    over60Owner: property.over_60_owner === 1,
    hasLivestock: property.has_livestock === 1,
    hasShortTermContractors: property.has_short_term_contractors === 1,
    regionHazards: parseRegionHazards(property.region_hazards_json),
    hemisphere: (property.hemisphere === 'southern' ? 'southern' : 'northern'),
    monthlyMode: property.monthly_task_mode === 1,
  });

  const t = now();
  // Gather ids of tasks we're about to soft-delete so we can cancel any
  // OS reminders the user set on them. Without this the OS would still
  // fire reminders for ids that no longer point to active rows.
  const toCancel = await db.getAllAsync<{ id: string }>(
    `SELECT id FROM task
     WHERE property_id = ? AND completed_at IS NULL AND deleted_at IS NULL
       AND is_custom = 0`,
    propertyId
  );

  // Completion-window projection — pushes just-done/skipped tasks past
  // their cadence window. Shared with previewReplanDiff (see
  // projectPlanWithCompletionWindows) so the preview matches reality.
  const projected = await projectPlanWithCompletionWindows(
    db,
    propertyId,
    plan.tasks,
    t
  );

  await db.withTransactionAsync(async () => {
    // Soft-delete pending, non-custom tasks so we don't lose completion history
    // or wipe out the user's own custom tasks.
    await db.runAsync(
      `UPDATE task SET deleted_at = ?, updated_at = ?
       WHERE property_id = ? AND completed_at IS NULL AND deleted_at IS NULL
         AND is_custom = 0`,
      t,
      t,
      propertyId
    );
    for (const { task, due_at: due } of projected.values()) {
      await db.runAsync(
        `INSERT INTO task (
          id, property_id, system_id, rule_key, title, why, time_minutes,
          difficulty, season, due_at, tier, icon, created_at, updated_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        newId(),
        propertyId,
        task.system_id,
        task.rule_key,
        task.title,
        task.why,
        task.time_minutes,
        task.difficulty,
        task.season,
        due,
        task.tier,
        task.icon,
        t,
        t
      );
    }
  });
  // Cancel reminders for tasks we just retired. Fire-and-forget so a
  // notification-permission denial doesn't block the regenerate flow.
  void Promise.all(toCancel.map((row) => cancelTaskReminder(row.id))).catch(() => {});
  // Stamp the replan timestamp on the property so the annual nudge
  // sweep knows when to next surface the "Time to replan" chip.
  await db.runAsync(
    `UPDATE property SET last_replan_at = ?, updated_at = ? WHERE id = ?`,
    t,
    t,
    propertyId
  );
  bumpDataRevision();
  return plan.tasks.length;
}

/** ~11 months in milliseconds. The replan-suggestion threshold — once
 *  this much time has passed since `last_replan_at`, the Home tab
 *  surfaces a chip nudging the user to re-tune the year. Set a hair
 *  under 12 months so the suggestion fires BEFORE the anniversary,
 *  giving the user a head start instead of waiting until they're a
 *  month overdue. */
const REPLAN_NUDGE_MS = 11 * 30 * 24 * 60 * 60 * 1000;

/**
 * Returns true when the active property's plan is older than the
 * REPLAN_NUDGE_MS threshold and the user could benefit from a refresh.
 * Cheap enough to call on every Home render; reads a single column.
 */
export function shouldSuggestReplan(property: PropertyRow | null): boolean {
  if (!property) return false;
  const last = property.last_replan_at ?? property.created_at;
  return Date.now() - last >= REPLAN_NUDGE_MS;
}

export interface PartitionedTasks {
  hero: ActiveTask | null;
  thisWeek: ActiveTask[];
  upcoming: ActiveTask[];
}

export function partitionTasks(tasks: ActiveTask[]): PartitionedTasks {
  if (tasks.length === 0) return { hero: null, thisWeek: [], upcoming: [] };
  const t = now();
  const dayMs = 24 * 60 * 60 * 1000;
  const startOfToday = new Date(new Date(t).setHours(0, 0, 0, 0)).getTime();
  const endOfToday = startOfToday + dayMs;
  const weekCut = t + WEEK_MS;

  // Hero = earliest task that's due today or overdue (or has no due date).
  // Anything pushed beyond today — including a freshly rescheduled task —
  // visibly moves out of the hero slot into This Week / Coming Up, which
  // is exactly what users expect when they tap "Reschedule → Tomorrow."
  //
  // Fallback: if nothing is due today, promote the soonest upcoming task to
  // hero so a newly onboarded user (or anyone who's caught up but has plans)
  // always sees one actionable item rather than an empty "all caught up"
  // card on day one.
  let heroIdx = -1;
  for (let i = 0; i < tasks.length; i++) {
    const task = tasks[i];
    if (task.due_at == null || task.due_at <= endOfToday) {
      heroIdx = i;
      break;
    }
  }
  // Fallback: if nothing's due today, promote the soonest upcoming task to
  // hero — but ONLY if it falls within the next ~30 days. Beyond that the
  // "all caught up" empty card is the truer state (user has nothing
  // pressing) and a hero pointing months out feels noisy.
  if (heroIdx < 0 && tasks.length > 0) {
    const monthCut = t + 30 * dayMs;
    const first = tasks[0];
    if (first.due_at == null || first.due_at <= monthCut) heroIdx = 0;
  }
  const hero = heroIdx >= 0 ? tasks[heroIdx] : null;
  const rest = heroIdx >= 0
    ? [...tasks.slice(0, heroIdx), ...tasks.slice(heroIdx + 1)]
    : tasks;

  // No upper cut on "upcoming" — if the user reschedules a task out to 90
  // days (or any custom horizon), it must still appear somewhere on the
  // home screen, otherwise the action looks like it deleted the task.
  // `getActiveTasks` already caps the SQL query, so we'll never overflow.
  const thisWeek: ActiveTask[] = [];
  const upcoming: ActiveTask[] = [];
  for (const task of rest) {
    if ((task.due_at ?? Infinity) <= weekCut) thisWeek.push(task);
    else upcoming.push(task);
  }
  // Soft fallback: if nothing landed in This Week and there is no hero,
  // pull a few of the nearest-due tasks forward so the home tab isn't
  // empty when the user has only longer-horizon work scheduled.
  if (!hero && thisWeek.length === 0 && rest.length > 0) {
    thisWeek.push(...rest.slice(0, 3));
    upcoming.length = 0;
    upcoming.push(...rest.slice(3, 15));
  }
  // Wide caps so a rescheduled task can't fall off the visible list. If
  // the user has, say, 20 tasks in the "Coming up" window and reschedules
  // one to the back, it must still render rather than silently dropping.
  return { hero, thisWeek: thisWeek.slice(0, 6), upcoming: upcoming.slice(0, 40) };
}

export function dueLabel(due_at: number | null | undefined): string {
  if (!due_at) return 'Whenever';
  const t = now();
  // Calendar-day buckets, not "hours from now" — otherwise a task pushed
  // to "tomorrow 9am" at 11am today still falls inside the 24h window and
  // labels "Due today," which is the bug users hit after Reschedule →
  // Tomorrow.
  const startOfToday = new Date(new Date(t).setHours(0, 0, 0, 0)).getTime();
  const dayMs = 86_400_000;
  if (due_at < startOfToday) return 'Overdue';
  if (due_at < startOfToday + dayMs) return 'Due today';
  if (due_at < startOfToday + 2 * dayMs) return 'Due tomorrow';
  const days = Math.floor((due_at - startOfToday) / dayMs);
  if (days < 7) return `in ${days} days`;
  if (days < 30) return `in ${Math.round(days / 7)} weeks`;
  if (days < 365) return `in ${Math.round(days / 30)} months`;
  return `in ${Math.round(days / 365)} years`;
}

export function currentSeasonLabel(date = new Date()): {
  name: 'Spring' | 'Summer' | 'Fall' | 'Winter';
  week: number;
} {
  const m = date.getMonth();
  const day = date.getDate();
  if (m === 2 && day >= 20) return { name: 'Spring', week: Math.ceil((day - 19) / 7) };
  if (m >= 3 && m <= 4) return { name: 'Spring', week: Math.ceil((m * 30 + day - 79) / 7) };
  if (m === 5 && day < 21) return { name: 'Spring', week: Math.ceil((m * 30 + day - 79) / 7) };
  if (m === 5) return { name: 'Summer', week: Math.ceil((day - 20) / 7) };
  if (m >= 6 && m <= 7) return { name: 'Summer', week: Math.ceil(((m - 5) * 30 + day) / 7) };
  if (m === 8 && day < 22) return { name: 'Summer', week: Math.ceil(((m - 5) * 30 + day) / 7) };
  if (m === 8) return { name: 'Fall', week: Math.ceil((day - 21) / 7) };
  if (m >= 9 && m <= 10) return { name: 'Fall', week: Math.ceil(((m - 8) * 30 + day - 21) / 7) };
  if (m === 11 && day < 21) return { name: 'Fall', week: Math.ceil(((m - 8) * 30 + day - 21) / 7) };
  if (m === 11) return { name: 'Winter', week: Math.ceil((day - 20) / 7) };
  return { name: 'Winter', week: Math.ceil((m * 30 + day) / 7) };
}

/**
 * Profile-completeness signals. Pure "have you told us about your
 * home?" — no engagement metrics, no lifestyle bonuses, no time-window
 * trickery. The profile percentage is meant to map 1:1 onto the data
 * the user has entered.
 */
export interface ProfileSignals {
  /** Number of property attribute fields the user has filled out of
   *  the total that matter (see PROFILE_ATTRIBUTE_FIELDS). */
  attributesFilled: number;
  attributesTotal: number;
  /** Count of system rows whose `kind` HAS subtype options defined in
   *  the catalog (water heater, range, dryer, fireplace, roof,
   *  generator, mower, gas grill). These are the systems where we
   *  actually need the user's input to schedule correctly. */
  subtypeEligibleSystems: number;
  /** Of those eligible systems, how many have the user picked a
   *  subtype. Chip selection — fast and meaningful, unlike typing
   *  install years for every ceiling fan. */
  subtypesSelected: number;
  /** Has the user completed at least one task ever? One-time ratchet. */
  hasCompletedAnyTask: boolean;
}

/**
 * Single source of truth for "what counts as a filled-out profile
 * attribute." Used by both the Home-tab ring (proportional weighting)
 * and the My Home health card (per-field listing with labels). Sharing
 * this list means the two surfaces can never disagree about which
 * fields count.
 *
 * Decisions baked in:
 *   - `climate_zone` is intentionally NOT here. It's auto-derived from
 *     ZIP via the climate-zip table, not user-entered. Counting it
 *     separately would double-credit ZIP entry.
 *   - `stories` is omitted because users rarely set it and it doesn't
 *     drive any rules today. Adding clutter for no payoff.
 *   - `background_uri` (home photo) is intentionally absent. Many users
 *     prefer the default image and we shouldn't penalize that choice
 *     — photo upload doesn't tell us how to maintain the home.
 *   - `name` (home nickname) is included because it powers the
 *     greeting and feels like "telling the app about my home."
 *   - `owner_first_name` is included for the same reason — personal
 *     identifier that feeds the greeting and PDF binder.
 *
 * Adding a field here: it'll automatically show up in the My Home
 * health card sheet AND start contributing to the Home ring. Make
 * sure the label is set in PROFILE_FIELD_LABELS below.
 */
export const PROFILE_ATTRIBUTE_FIELDS: (keyof PropertyRow)[] = [
  'name',
  'zip',
  'address',
  'home_type',
  // Precise build year, captured in step 2 of onboarding. Replaces the
  // older year_band bucket in profile counting — the band is still
  // persisted (derived from year_built when set) so rule paths that
  // key on it keep working.
  'year_built',
  'sqft',
  'stories',
  'bedrooms',
  'bathrooms',
  'hvac_type',
  'heating_fuel',
  'lot_band',
  'basement',
  'foundation',
  'roof_material',
  'exterior_material',
  'garage',
  'water_source',
  'sewer',
  'owner_first_name',
];

/**
 * Human labels for each tracked profile attribute. Used by the My Home
 * health card to render "X is still blank" lists. Keep in sync with
 * PROFILE_ATTRIBUTE_FIELDS above — TypeScript won't enforce one-to-one
 * mapping so be careful when editing.
 */
export const PROFILE_FIELD_LABELS: Record<string, string> = {
  name: 'Home nickname',
  zip: 'ZIP code (climate)',
  address: 'Street address (map)',
  home_type: 'Home type',
  year_band: 'Build era',
  year_built: 'Build year',
  sqft: 'Square footage',
  stories: 'Stories above ground',
  bedrooms: 'Bedrooms',
  bathrooms: 'Bathrooms',
  hvac_type: 'Primary HVAC',
  heating_fuel: 'Heating fuel',
  lot_band: 'Lot size',
  basement: 'Basement type',
  foundation: 'Foundation',
  roof_material: 'Roof material',
  exterior_material: 'Exterior material',
  garage: 'Garage',
  water_source: 'Water source',
  sewer: 'Sewer / septic',
  owner_first_name: 'Your first name',
};

/** True when a profile attribute value reads as "filled" for the
 *  profile-completion signal.
 *
 *  Note: 'unsure' / 'unknown' COUNT as filled here. The user committed
 *  to an answer — "I don't know" is information, and we don't want to
 *  punish them with a forever-stuck-below-100 ring for being honest.
 *  The rule engine reads the raw column values separately and still
 *  treats 'unsure' as missing for its scheduling (e.g. septic-pumping
 *  rules won't fire without a real water/sewer value), so this only
 *  affects the profile percentage. */
export function isProfileFieldFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  if (typeof value === 'number') return value > 0;
  return true;
}

/**
 * Per-field check: which attributes are filled, which are blank.
 * Returned in PROFILE_ATTRIBUTE_FIELDS order so the My Home card can
 * render the blanks list in a stable sequence (no jumping around
 * after each fill).
 */
export interface ProfileAttributeCheck {
  field: keyof PropertyRow;
  label: string;
  filled: boolean;
}

export function profileAttributeChecks(
  property: PropertyRow
): ProfileAttributeCheck[] {
  return PROFILE_ATTRIBUTE_FIELDS.map((field) => ({
    field,
    label: PROFILE_FIELD_LABELS[field] ?? String(field),
    filled: isProfileFieldFilled(property[field]),
  }));
}

/**
 * Convenience tally — used by `useHomeData` to feed `computeProfile`.
 * Returns the same numbers My Home shows ("X of Y attributes filled"),
 * so the two surfaces always agree on attribute coverage.
 */
export function countProfileAttributes(
  property: PropertyRow
): { filled: number; total: number } {
  const checks = profileAttributeChecks(property);
  return {
    filled: checks.filter((c) => c.filled).length,
    total: checks.length,
  };
}

/**
 * Compute the home profile percentage shown on the Home screen ring.
 *
 * Weight breakdown (sums to 100):
 *   80 — Property attribute completeness (proportional, N fields).
 *        This is "Edit my Home" — the main onboarding output. Filling
 *        every attribute is the single largest contributor.
 *   20 — Subtype coverage across systems that HAVE subtype options.
 *        Chip selection (e.g. tank vs tankless water heater) is fast
 *        and high-leverage — it directly drives task selection.
 *        Systems without subtype options (most of them) don't enter
 *        this calculation, so users aren't penalized for the catalog.
 *        When NO eligible systems exist at all, this 20pt is awarded
 *        in full (you can't fail at something the catalog doesn't ask).
 *
 * Why this shape (v4, after user feedback):
 *   - The earlier v3 awarded 10pt the instant a user completed any task.
 *     But profile completeness should reflect HOW MUCH HOME DATA we have,
 *     not engagement. A user who completes a task but still has a blank
 *     ZIP shouldn't ratchet up — the data isn't there. Engagement lives
 *     on the streak chip, not on the profile ring.
 *   - v2 demanded model #, install year, filter size, etc. on every
 *     system. Users with 17 ceiling fans were dragged below 70%
 *     even after filling every property attribute. That's wrong:
 *     model and install year don't matter for a ceiling fan.
 *   - Subtype matters everywhere it exists (it changes which tasks
 *     apply), and it's CHEAP to provide — one chip tap. So subtype
 *     coverage is a fair, leverage-weighted signal.
 *   - Per-system depth (model, year, filter) lives in the /profile-setup
 *     walkthrough and the My Home health card subtitle — it's
 *     surfaced as a *tune* path, not a *required* path.
 */
export function computeProfile(
  _property: PropertyRow,
  signals: ProfileSignals
): number {
  let pct = 0;

  // 80pt — Property attribute completeness.
  if (signals.attributesTotal > 0) {
    pct += Math.round((signals.attributesFilled / signals.attributesTotal) * 80);
  }

  // 20pt — Subtype coverage. When the user has zero subtype-eligible
  // systems, award full credit (we don't want to hold a user hostage
  // to the catalog's design — not every home has a fireplace).
  if (signals.subtypeEligibleSystems === 0) {
    pct += 20;
  } else {
    pct += Math.round(
      (signals.subtypesSelected / signals.subtypeEligibleSystems) * 20
    );
  }

  // Note: profile % is purely about HOW MUCH HOME DATA we have. Task
  // completion is a separate signal that lives on the streak chip and
  // the activity views — it doesn't ratchet this number.

  return Math.min(100, Math.max(0, pct));
}

export interface CustomTaskInput {
  propertyId: string;
  systemId?: string | null;
  title: string;
  why?: string | null;
  time_minutes?: number | null;
  difficulty?: Difficulty | null;
  icon?: string | null;
  tier?: Tier | null;
  due_at: number;
  cadence_days?: number | null; // null = one-off
  supplies?: string[];
  steps?: string[];
  tips?: string[];
}

export async function addCustomTask(
  db: SQLiteDatabase,
  input: CustomTaskInput
): Promise<string> {
  const id = newId();
  const t = now();
  await db.runAsync(
    `INSERT INTO task (
      id, property_id, system_id, rule_key, title, why, time_minutes,
      difficulty, season, due_at, tier, icon, is_custom, cadence_days,
      supplies_json, steps_json, tips_json, created_at, updated_at
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, 'any', ?, ?, ?, 1, ?, ?, ?, ?, ?, ?)`,
    id,
    input.propertyId,
    input.systemId ?? null,
    `custom:${id}`,
    input.title.trim(),
    input.why ?? null,
    input.time_minutes ?? null,
    input.difficulty ?? 'easy',
    input.due_at,
    input.tier ?? null,
    input.icon ?? 'Sparkles',
    input.cadence_days ?? null,
    input.supplies && input.supplies.length ? JSON.stringify(input.supplies) : null,
    input.steps && input.steps.length ? JSON.stringify(input.steps) : null,
    input.tips && input.tips.length ? JSON.stringify(input.tips) : null,
    t,
    t
  );
  return id;
}

/**
 * Re-schedule a recurring custom task after completion. Called from the
 * completion sheet — if the task has a cadence, we plant a fresh row with a
 * new due date so the user sees it again at the right interval.
 */
export async function rescheduleCustomFollowUp(
  db: SQLiteDatabase,
  taskId: string
): Promise<void> {
  const original = await db.getFirstAsync<TaskRow>(
    `SELECT * FROM task WHERE id = ?`,
    taskId
  );
  if (!original || !original.is_custom || !original.cadence_days) return;
  const id = newId();
  const t = now();
  const next = t + original.cadence_days * 24 * 60 * 60 * 1000;
  await db.runAsync(
    `INSERT INTO task (
      id, property_id, system_id, rule_key, title, why, time_minutes,
      difficulty, season, due_at, tier, icon, is_custom, cadence_days,
      supplies_json, steps_json, tips_json, created_at, updated_at
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, 'any', ?, ?, ?, 1, ?, ?, ?, ?, ?, ?)`,
    id,
    original.property_id,
    original.system_id,
    original.rule_key,
    original.title,
    original.why,
    original.time_minutes,
    original.difficulty,
    next,
    original.tier,
    original.icon,
    original.cadence_days,
    original.supplies_json,
    original.steps_json,
    original.tips_json,
    t,
    t
  );
}

export async function resetPropertyData(db: SQLiteDatabase): Promise<void> {
  await db.withTransactionAsync(async () => {
    // Every property-scoped domain table, children first. FKs are per-
    // connection and cascades historically weren't firing, so we delete
    // explicitly rather than trusting ON DELETE CASCADE — a "reset
    // everything" that leaves expenses/quotes/notes behind isn't one.
    for (const table of [
      'task_completion',
      'task',
      'system_reminder',
      'system',
      'document',
      'contact',
      'inventory_item',
      'expense',
      'quote',
      'goal',
      'note',
      'message',
      'property',
    ]) {
      // Tolerate tables that don't exist on older schemas.
      await db.runAsync(`DELETE FROM ${table}`).catch(() => {});
    }
    await db
      .runAsync(`UPDATE user_settings SET active_property_id = NULL`)
      .catch(() => {});
  });
  // Disarm everything the OS still has queued — reminders for rows that
  // no longer exist would keep firing otherwise.
  void cancelAllDigests().catch(() => {});
  void cancelAllTaskReminders().catch(() => {});
  void cancelAllSystemReminders().catch(() => {});
  bumpDataRevision();
}

// Backward-compat alias for the home screen's existing usage.
export { PlannedTask };
